import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Button from '../components/ui/Button';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from '../layouts/MainLayout';
import { apiService } from '../services/api';
import { toast } from 'react-toastify';

interface User {
  id: number;
  name: string;
  username: string;
  email: string;
  group_id: number;
  group_name?: string;
  group?: {
    id: number;
    title: string;
  };
}

interface Group {
  id: number;
  title: string;
  description: string;
}

interface Module {
  id: number;
  title: string;
  route: string;
  parent_id: number | null;
  level: string;
  position: number;
  parent?: {
    id: number;
    title: string;
  };
}

interface Permission {
  module_id: number;
  module_name: string;
  module_route: string;
  can_add: boolean;
  can_edit: boolean;
  can_delete: boolean;
  can_print: boolean;
  can_export: boolean;
}

const Permissions: React.FC = () => {
  const { type, id } = useParams<{ type: string; id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  
  const [item, setItem] = useState<User | Group | null>(null);
  const [modules, setModules] = useState<Module[]>([]);
  const [permissions, setPermissions] = useState<Map<number, Permission>>(new Map());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<number | null>(null);

  // Cache modules to avoid refetching on each render
  const cachedModules = useMemo(() => modules, [modules]);

  useEffect(() => {
    const fetchData = async () => {
      if (!id || !type) return;
      
      setLoading(true);
      try {
        // Fetch item data (user or group)
        const itemResponse = await apiService.get<{ item: User | Group }>(`${type}s/${id}`);
        if (itemResponse.success) {
          setItem(itemResponse.data.item);
        }

        // Fetch all published modules with parent information (cached)
        const modulesResponse = await apiService.get<{ items: Module[] }>('modules', { 
          per_page: 1000,
          published: 1 // Only published modules
        });
        if (modulesResponse.success) {
          setModules(modulesResponse.data.items);
          
          // Set active tab to first parent module
          const firstParent = modulesResponse.data.items.find((m: Module) => m.parent_id === null);
          if (firstParent) {
            setActiveTab(firstParent.id);
          }
        }

        // Fetch permissions
        const permissionsResponse = await apiService.get<any>(`${type}s/${id}/permissions`);
        
        if (permissionsResponse.success && permissionsResponse.data) {
          const permissionsMap = new Map();
          
          if (type === 'user') {
            // Handle user permissions (they take precedence over group permissions)
            if (permissionsResponse.data.user_permissions) {
              permissionsResponse.data.user_permissions.forEach((perm: Permission) => {
                permissionsMap.set(perm.module_id, perm);
              });
            }
            
            // Handle group permissions (only if no user permission exists for that module)
            if (permissionsResponse.data.group_permissions) {
              permissionsResponse.data.group_permissions.forEach((perm: Permission) => {
                if (!permissionsMap.has(perm.module_id)) {
                  permissionsMap.set(perm.module_id, perm);
                }
              });
            }
          } else if (type === 'group') {
            // Handle group permissions (simple structure)
            if (permissionsResponse.data.permissions) {
              permissionsResponse.data.permissions.forEach((perm: Permission) => {
                permissionsMap.set(perm.module_id, perm);
              });
            }
          }
          
          setPermissions(permissionsMap);
        } else {
          setPermissions(new Map());
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load permissions data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, type]);

  const handlePermissionChange = (moduleId: number, permission: keyof Permission, value: boolean) => {
    setPermissions(prev => {
      const newPermissions = new Map(prev);
      const existing = newPermissions.get(moduleId);
      
      if (existing) {
        newPermissions.set(moduleId, { ...existing, [permission]: value });
      } else {
        const module = cachedModules.find(m => m.id === moduleId);
        newPermissions.set(moduleId, {
          module_id: moduleId,
          module_name: module?.title || '',
          module_route: module?.route || '',
          can_add: false,
          can_edit: false,
          can_delete: false,
          can_print: false,
          can_export: false,
          [permission]: value,
        });
      }
      
      return newPermissions;
    });
  };

  const handleCheckAllColumn = (permission: keyof Permission, value: boolean) => {
    const level2Modules = getLevel2Modules(activeTab);
    const level3Modules = level2Modules.flatMap(l2 => getLevel3Modules(l2.id));
    const allModuleIds = [
      ...level2Modules.map(m => m.id),
      ...level3Modules.map(m => m.id)
    ];
    
    allModuleIds.forEach(moduleId => {
      handlePermissionChange(moduleId, permission, value);
    });
  };

  const savePermissions = async () => {
    if (!id || !type) return;
    
    setSaving(true);
    
    try {
      // Get all modules (both level 2 and level 3) for all tabs
      const allParentModules = getLevel2Modules(null);
      const allPermissions: any[] = [];
      
      // Collect all permissions for all modules
      allParentModules.forEach(parentModule => {
        const level2Modules = getLevel2Modules(parentModule.id);
        const level3Modules = level2Modules.flatMap(l2 => getLevel3Modules(l2.id));
        const allModuleIds = [
          ...level2Modules.map(m => m.id),
          ...level3Modules.map(m => m.id)
        ];
        
        allModuleIds.forEach(moduleId => {
          const perm = getPermissionForModule(moduleId);
          allPermissions.push({
            module_id: perm.module_id,
            can_add: perm.can_add,
            can_edit: perm.can_edit,
            can_delete: perm.can_delete,
            can_print: perm.can_print,
            can_export: perm.can_export,
          });
        });
      });
      
      const response = await apiService.put(`${type}s/${id}/permissions`, {
        permissions: allPermissions
      });
      
      if (response.success) {
        toast.success('Permissions saved successfully');
      } else {
        toast.error('Failed to save permissions');
      }
    } catch (error) {
      console.error('Error saving permissions:', error);
      toast.error('Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  const getPermissionForModule = (moduleId: number): Permission => {
    const found = permissions.get(moduleId);
    if (found) return found;
    
    const module = cachedModules.find(m => m.id === moduleId);
    return {
      module_id: moduleId,
      module_name: module?.title || '',
      module_route: module?.route || '',
      can_add: false,
      can_edit: false,
      can_delete: false,
      can_print: false,
      can_export: false,
    };
  };

  const getLevel2Modules = (parentId: number | null) => {
    return cachedModules.filter(m => m.parent_id === parentId).sort((a, b) => a.position - b.position);
  };

  const getLevel3Modules = (level2Id: number) => {
    return cachedModules.filter(m => m.parent_id === level2Id).sort((a, b) => a.position - b.position);
  };

  const getItemName = () => {
    if (!item) return '';
    if ('name' in item) return (item as User).name;
    if ('title' in item) return (item as Group).title;
    return '';
  };

  const getItemType = () => {
    return type === 'user' ? 'User' : 'Group';
  };

  const areAllPermissionsChecked = (permission: keyof Permission): boolean => {
    const level2Modules = getLevel2Modules(activeTab);
    const level3Modules = level2Modules.flatMap(l2 => getLevel3Modules(l2.id));
    const allModuleIds = [
      ...level2Modules.map(m => m.id),
      ...level3Modules.map(m => m.id)
    ];
    
    return allModuleIds.every(moduleId => {
      const perm = getPermissionForModule(moduleId);
      return perm[permission];
    });
  };

  const areSomePermissionsChecked = (permission: keyof Permission): boolean => {
    const level2Modules = getLevel2Modules(activeTab);
    const level3Modules = level2Modules.flatMap(l2 => getLevel3Modules(l2.id));
    const allModuleIds = [
      ...level2Modules.map(m => m.id),
      ...level3Modules.map(m => m.id)
    ];
    
    return allModuleIds.some(moduleId => {
      const perm = getPermissionForModule(moduleId);
      return perm[permission];
    });
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6">
          <div className="text-center py-8">Loading permissions...</div>
        </div>
      </MainLayout>
    );
  }

  if (!item) {
    return (
      <MainLayout>
        <div className="p-6">
          <div className="text-center py-8">Item not found</div>
        </div>
      </MainLayout>
    );
  }

  const parentModules = getLevel2Modules(null);

  return (
    <MainLayout>
      <div className="p-6">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {getItemType()} Permissions
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Manage permissions for {getItemName()}
              </p>
            </div>
            <Button
              onClick={() => navigate(-1)}
              className="bg-gray-500 hover:bg-gray-600"
            >
              Back
            </Button>
          </div>
        </div>

        {/* Item Info */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {getItemName()}
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                {getItemType()} ID: {id}
              </p>
              {'group' in item && item.group && (
                <p className="text-gray-600 dark:text-gray-400">
                  Group: {(item as User).group?.title}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex space-x-8 px-6">
              {parentModules.map((parentModule) => (
                <button
                  key={parentModule.id}
                  onClick={() => setActiveTab(parentModule.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === parentModule.id
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  {parentModule.title}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {activeTab && (
              <>
                {/* Modules Grid for Current Tab */}
                <div className="space-y-6">
                  <table className="min-w-full border border-gray-200 dark:border-gray-700 mb-6 text-sm">
                    <thead className="bg-gray-100 dark:bg-gray-700">
                      <tr>
                        <th className="px-4 py-2 text-left font-semibold">Module</th>
                        <th className="px-4 py-2 text-left font-semibold">Parent</th>
                        {(['can_edit', 'can_print', 'can_export'] as const).map((perm) => {
                          const allChecked = areAllPermissionsChecked(perm);
                          const someChecked = areSomePermissionsChecked(perm);
                          return (
                            <th key={perm} className="px-4 py-2 text-center font-semibold">
                              <div className="flex items-center justify-center">
                                <input
                                  type="checkbox"
                                  checked={allChecked}
                                  ref={input => { 
                                    if (input) input.indeterminate = someChecked && !allChecked; 
                                  }}
                                  onChange={e => handleCheckAllColumn(perm, e.target.checked)}
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="ml-2 capitalize">{perm.replace('_', ' ')}</span>
                              </div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {/* Level 2 modules */}
                      {getLevel2Modules(activeTab).map(level2 => (
                        <React.Fragment key={level2.id}>
                          <tr className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                            <td className="px-4 py-2 font-medium text-gray-900 dark:text-white">{level2.title}</td>
                            <td className="px-4 py-2 text-gray-600 dark:text-gray-400">
                              {level2.parent?.title || '-'}
                            </td>
                            {(['can_edit', 'can_print', 'can_export'] as const).map(perm => (
                              <td key={perm} className="px-4 py-2 text-center">
                                <div className="flex items-center justify-center">
                                  <input
                                    type="checkbox"
                                    checked={getPermissionForModule(level2.id)[perm]}
                                    onChange={e => handlePermissionChange(level2.id, perm, e.target.checked)}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  />
                                  <span className="ml-2 opacity-0">{perm.replace('_', ' ')}</span>
                                </div>
                              </td>
                            ))}
                          </tr>
                          {/* Level 3 modules under this Level 2 */}
                          {getLevel3Modules(level2.id).map(level3 => (
                            <tr key={level3.id} className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
                              <td className="px-4 py-2 pl-8 text-gray-800 dark:text-white">{level3.title}</td>
                              <td className="px-4 py-2 text-gray-600 dark:text-gray-400">
                                {level3.parent?.title || '-'}
                              </td>
                              {(['can_edit', 'can_print', 'can_export'] as const).map(perm => (
                                <td key={perm} className="px-4 py-2 text-center">
                                  <div className="flex items-center justify-center">
                                    <input
                                      type="checkbox"
                                      checked={getPermissionForModule(level3.id)[perm]}
                                      onChange={e => handlePermissionChange(level3.id, perm, e.target.checked)}
                                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="ml-2 opacity-0">{perm.replace('_', ' ')}</span>
                                  </div>
                                </td>
                              ))}
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700 mt-6">
              <Button
                onClick={savePermissions}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Permissions'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default Permissions; 